import { useState, useEffect, useCallback } from 'react'
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  updatePassword,
} from 'aws-amplify/auth'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: { email: string } | null
  error: string | null
  needsNewPassword: boolean
  needsPasswordReset: boolean
  resetEmail: string | null
}

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
  needsNewPassword: false,
  needsPasswordReset: false,
  resetEmail: null,
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(initialState)

  const checkAuth = useCallback(async () => {
    try {
      const user = await getCurrentUser()
      const session = await fetchAuthSession()

      if (user && session.tokens) {
        setState({
          ...initialState,
          isAuthenticated: true,
          isLoading: false,
          user: { email: user.signInDetails?.loginId || '' },
        })
      } else {
        setState({
          ...initialState,
          isLoading: false,
        })
      }
    } catch {
      setState({
        ...initialState,
        isLoading: false,
      })
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const login = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }))
    try {
      // Clear any existing session before attempting new sign in
      // This prevents "There is already a signed in user" errors
      try {
        await signOut()
      } catch {
        // Ignore errors - there may not be an existing session
      }

      const result = await signIn({ username: email, password })

      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          needsNewPassword: true,
          error: null,
        }))
        return { needsNewPassword: true }
      }

      await checkAuth()
      return { needsNewPassword: false }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed'

      // Check if user needs to reset password (RESET_REQUIRED state)
      if (errorMessage.includes('Password reset required')) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          needsPasswordReset: true,
          resetEmail: email,
          error: null,
        }))
        return { needsPasswordReset: true }
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      throw err
    }
  }

  const completeNewPassword = async (newPassword: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }))
    try {
      await confirmSignIn({ challengeResponse: newPassword })
      setState((prev) => ({ ...prev, needsNewPassword: false }))
      await checkAuth()
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to set new password',
      }))
      throw err
    }
  }

  // Initiate password reset flow (forgot password)
  const initiatePasswordReset = async (email: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }))
    try {
      await resetPassword({ username: email })
      setState((prev) => ({
        ...prev,
        isLoading: false,
        needsPasswordReset: true,
        resetEmail: email,
      }))
      return { success: true }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to initiate password reset',
      }))
      throw err
    }
  }

  // Complete password reset with code and new password
  const completePasswordReset = async (code: string, newPassword: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }))
    try {
      if (!state.resetEmail) {
        throw new Error('No email address for password reset')
      }
      await confirmResetPassword({
        username: state.resetEmail,
        confirmationCode: code,
        newPassword,
      })
      setState((prev) => ({
        ...prev,
        isLoading: false,
        needsPasswordReset: false,
        resetEmail: null,
      }))
      return { success: true }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to reset password',
      }))
      throw err
    }
  }

  const cancelPasswordReset = () => {
    setState((prev) => ({
      ...prev,
      needsPasswordReset: false,
      resetEmail: null,
      error: null,
    }))
  }

  const logout = async () => {
    try {
      await signOut()
      setState({
        ...initialState,
        isLoading: false,
      })
    } catch (err) {
      console.error('Logout error:', err)
    }
  }

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString() || null
    } catch {
      return null
    }
  }, [])

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await updatePassword({ oldPassword, newPassword })
      return { success: true }
    } catch (err) {
      throw err
    }
  }

  return {
    ...state,
    login,
    logout,
    getToken,
    checkAuth,
    completeNewPassword,
    initiatePasswordReset,
    completePasswordReset,
    cancelPasswordReset,
    changePassword,
  }
}
